// src/pages/WordPress/WordPressEnvironments.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { WordPressService } from '../../types/service.type';
import ServiceCard from '../../components/shared';

const WordPressEnvironments: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'running' | 'stopped'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Mock data - Convertido a WordPressService
  const [environments] = useState<WordPressService[]>([
    {
      id: '1',
      name: 'my-blog',
      type: 'wordpress',
      status: 'running',
      health: 'healthy',
      primaryUrl: 'https://my-blog.kubiy.cloud',
      adminUrl: 'https://my-blog.kubiy.cloud/wp-admin',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-20'),
      lastActionAt: new Date('2024-01-20'),
      instanceType: 'wp-medium',
      region: 'us-east-1',
      environment: 'production',
      tags: ['blog', 'production'],
      phpVersion: 'PHP 8.2',
      wpVersion: '6.4',
      sslEnabled: true,
      config: {} as any,
    },
    {
      id: '2',
      name: 'company-site',
      type: 'wordpress',
      status: 'running',
      health: 'healthy',
      primaryUrl: 'https://company-site.kubiy.cloud',
      adminUrl: 'https://company-site.kubiy.cloud/wp-admin',
      createdAt: new Date('2024-01-10'),
      updatedAt: new Date('2024-01-18'),
      instanceType: 'wp-large',
      region: 'us-west-2',
      environment: 'production',
      phpVersion: 'PHP 8.1',
      wpVersion: '6.4',
      sslEnabled: true,
      config: {} as any,
    },
    {
      id: '3',
      name: 'test-environment',
      type: 'wordpress',
      status: 'stopped',
      health: 'unknown',
      primaryUrl: 'https://test-environment.kubiy.cloud',
      adminUrl: 'https://test-environment.kubiy.cloud/wp-admin',
      createdAt: new Date('2024-01-05'),
      updatedAt: new Date('2024-01-10'),
      instanceType: 'wp-small',
      region: 'us-east-1',
      environment: 'development',
      phpVersion: 'PHP 8.2',
      wpVersion: '6.3',
      sslEnabled: true,
      config: {} as any,
    },
  ]);

  const filteredEnvironments = environments.filter((env) => {
    const matchesSearch = env.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (env.primaryUrl && env.primaryUrl.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || env.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleActionClick = (action: string, service: WordPressService) => {
    console.log(`Action ${action} on service ${service.name}`);
  };

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">WordPress Environments</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage and monitor your WordPress applications
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ReloadOutlined />
              Refresh
            </button>
            <button
              onClick={() => navigate('/resources/create-wordpress')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusOutlined />
              Create Environment
            </button>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search environments by name or domain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              All ({environments.length})
            </button>
            <button
              onClick={() => setFilterStatus('running')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'running'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Running ({environments.filter(e => e.status === 'running').length})
            </button>
            <button
              onClick={() => setFilterStatus('stopped')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'stopped'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Stopped ({environments.filter(e => e.status === 'stopped').length})
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title="Grid view"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title="List view"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredEnvironments.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No environments found</h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first WordPress environment'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => navigate('/resources/create-wordpress')}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <PlusOutlined />
              Create Environment
            </button>
          )}
        </div>
      )}

      {/* Grid View */}
      {filteredEnvironments.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEnvironments.map((env) => (
            <ServiceCard
              key={env.id}
              service={env}
              variant="default"
              onClick={() => navigate(`/services/${env.id}`)}
              onActionClick={(action: any) => handleActionClick(action, env)}
              renderExtra={(service: any) => {
                // ✅ TYPE GUARD: Verificar que es WordPressService
                if (service.type === 'wordpress') {
                  const wpService = service as WordPressService;
                  return (
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{wpService.phpVersion} • WP {wpService.wpVersion}</span>
                      {wpService.sslEnabled && (
                        <span className="text-green-600 font-medium">🔒 SSL</span>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
          ))}
        </div>
      )}

      {/* List View */}
      {filteredEnvironments.length > 0 && viewMode === 'list' && (
        <div className="space-y-4">
          {filteredEnvironments.map((env) => (
            <ServiceCard
              key={env.id}
              service={env}
              variant="compact"
              onClick={() => navigate(`/services/${env.id}`)}
              onActionClick={(action: any) => handleActionClick(action, env)}
            />
          ))}
        </div>
      )}

      {/* Stats Summary */}
      {filteredEnvironments.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{environments.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Running</div>
            <div className="text-2xl font-bold text-green-600">
              {environments.filter(e => e.status === 'running').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Stopped</div>
            <div className="text-2xl font-bold text-gray-600">
              {environments.filter(e => e.status === 'stopped').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Healthy</div>
            <div className="text-2xl font-bold text-green-600">
              {environments.filter(e => e.health === 'healthy').length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordPressEnvironments;